import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect, useCallback } from 'react';

import {
  Box,
  Tab,
  Card,
  Tabs,
  Stack,
  alpha,
  styled,
  Button,
  Container,
  Typography,
  OutlinedInput,
  InputAdornment,
  CircularProgress,
} from '@mui/material';

import { useToast } from 'src/hooks/useToast';
import { usePermission } from 'src/hooks/usePermission';

import {
  fetchRoles,
  bulkSyncRole,
  unarchiveRole,
  getRoleConflicts,
  syncRolePermission,
  fetchAllPermissions,
  archiveAndMigrateRole,
  getRolePermissionStrings,
} from 'src/services/role.service';

import Iconify from 'src/components/iconify';

import RoleTable from './subSections/RoleTable';
import RoleEditDialog from './dialogs/RoleEditDialog';
import ConflictDialog from './dialogs/ConflictDialog';
import RoleCreateView from './subSections/RoleCreateView';
import RoleArchiveDialog from './dialogs/RoleArchiveDialog';
import RolePermissionEditor from './subSections/RolePermissionEditor';

const StyledSearch = styled(OutlinedInput)(({ theme }) => ({
  width: 240,
  transition: theme.transitions.create(['box-shadow', 'width'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  '&.Mui-focused': {
    width: 320,
    boxShadow: `0 8px 16px 0 ${alpha(theme.palette.common.black, 0.1)}`,
  },
  '& fieldset': {
    borderWidth: `1px !important`,
    borderColor: `${alpha(theme.palette.grey[500], 0.32)} !important`,
  },
  '&.Mui-focused fieldset': {
    borderColor: `${alpha(theme.palette.grey[500], 0.45)} !important`,
  },
}));

export default function RolesView() {
  const { t } = useTranslation();
  const { showToast, hideToast } = useToast();
  const { can } = usePermission();

  const [activeTab, setActiveTab] = useState('active');
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [isEditingPermissions, setIsEditingPermissions] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedRolePerms, setSelectedRolePerms] = useState([]);
  const [systemPerms, setSystemPerms] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const [pendingChanges, setPendingChanges] = useState([]);
  const [conflict, setConflict] = useState({
    open: false,
    users: [],
    permId: null,
    permName: '',
    isAdding: false,
  });

  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);

  const canCreateRole = can({ permission: 'ROLES:CREATE' });
  const canUpdateRole = can({ permission: 'ROLES:UPDATE' });
  const canDeleteRole = can({ permission: 'ROLES:DELETE' });
  const canManagePermissions = can({ permission: 'PERMISSIONS:UPDATE' });

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await fetchRoles();
      setRoles(data);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    fetchAllPermissions().then(setSystemPerms);
  }, [loadData]);

  const processNextChange = useCallback(
    async (currentQueue) => {
      if (currentQueue.length === 0) {
        setPendingChanges([]);
        setIsEditingPermissions(false);
        setSelectedRole(null);
        loadData();
        showToast({
          message: t('rolesPage.permissionSyncSuccess'),
          severity: 'success',
          props: { hideToast },
        });
        return;
      }

      const [nextChange, ...remainingQueue] = currentQueue;
      const permEntity = systemPerms.find((p) => p.permissionName === nextChange.perm);
      if (!permEntity) {
        processNextChange(remainingQueue);
        return;
      }

      try {
        // 1. Fetch any manual overrides (conflicts) for this specific permission change
        const conflicts = await getRoleConflicts(
          selectedRole.id,
          permEntity.id,
          nextChange.isAdding
        );

        if (conflicts.length > 0) {
          // 2. Conflict found -> Halt the queue and open the Conflict Dialog
          setPendingChanges(currentQueue); // Save the halted queue state
          setConflict({
            open: true,
            users: conflicts,
            permId: permEntity.id,
            permName: nextChange.perm,
            isAdding: nextChange.isAdding,
          });
        } else {
          // 3. No conflict -> Commit changes silently and move to the next item in the queue
          await syncRolePermission(
            selectedRole.id,
            permEntity.id,
            nextChange.isAdding,
            []
          );

          setSelectedRolePerms((prev) => {
            let updated = nextChange.isAdding
              ? [...prev, nextChange.perm]
              : prev.filter((p) => p !== nextChange.perm);
            if (nextChange.isAdding && nextChange.perm.startsWith('DASHBOARD:')) {
              updated = updated.filter(
                (p) => !p.startsWith('DASHBOARD:') || p === nextChange.perm
              );
            }
            return Array.from(new Set(updated));
          });

          processNextChange(remainingQueue);
        }
      } catch (e) {
        console.error('Queue execution error:', e);
        setPendingChanges([]);
      }
    },
    [selectedRole, systemPerms, loadData, t, showToast, hideToast]
  );

  const handleSaveAllPermissions = async (newPerms) => {
    const toAdd = newPerms.filter((p) => !selectedRolePerms.includes(p));
    const toRemove = selectedRolePerms.filter((p) => !newPerms.includes(p));

    const allChanges = [
      ...toRemove.map((p) => ({ perm: p, isAdding: false })),
      ...toAdd.map((p) => ({ perm: p, isAdding: true })),
    ];

    if (allChanges.length === 0) {
      setIsEditingPermissions(false);
      setSelectedRole(null);
      return;
    }

    processNextChange(allChanges);
  };

  const handleConfirmConflict = async (overrideUserIds) => {
    try {
      await syncRolePermission(
        selectedRole.id,
        conflict.permId,
        conflict.isAdding,
        overrideUserIds
      );

      setSelectedRolePerms((prev) => {
        let updated = conflict.isAdding
          ? [...prev, conflict.permName]
          : prev.filter((p) => p !== conflict.permName);

        if (conflict.isAdding && conflict.permName.startsWith('DASHBOARD:')) {
          updated = updated.filter(
            (p) => !p.startsWith('DASHBOARD:') || p === conflict.permName
          );
        }
        return Array.from(new Set(updated));
      });

      setConflict({ ...conflict, open: false });

      // Resume queue: Process the remaining items in the queue
      const [, ...remaining] = pendingChanges;
      processNextChange(remaining);
    } catch (e) {
      console.error(e);
    }
  };

  const handleRestore = async (role) => {
    try {
      await unarchiveRole(role.id);
      loadData();
      showToast({
        message: t('rolesPage.updateSuccess'),
        severity: 'success',
        props: { hideToast },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleBulkSync = async (role) => {
    try {
      await bulkSyncRole(role.id);
      loadData();
      showToast({
        message: t('rolesPage.permissionSyncSuccess'),
        severity: 'success',
        props: { hideToast },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const confirmDirectArchive = async (id) => {
    try {
      await archiveAndMigrateRole(id, 0);
      loadData();
      showToast({
        message: t('rolesPage.updateSuccess'),
        severity: 'success',
        props: { hideToast },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleArchiveInitiate = (role) => {
    setSelectedRole(role);
    if (role.userCount > 0) {
      setIsArchiveDialogOpen(true);
    } else {
      confirmDirectArchive(role.id);
    }
  };

  const handleMigrationConfirm = async (targetRoleId) => {
    try {
      await archiveAndMigrateRole(selectedRole.id, targetRoleId);
      setIsArchiveDialogOpen(false);
      loadData();
      showToast({
        message: t('rolesPage.updateSuccess'),
        severity: 'success',
        props: { hideToast },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const filteredRoles = useMemo(
    () =>
      roles.filter((r) => {
        const status = r.active !== undefined ? r.active : r.isActive;
        return (
          (activeTab === 'active' ? status : !status) &&
          r.roleName.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }),
    [roles, activeTab, searchQuery]
  );

  if (isCreating)
    return (
      <RoleCreateView
        onCancel={() => setIsCreating(false)}
        onSuccess={() => {
          setIsCreating(false);
          loadData();
          showToast({
            message: t('rolesPage.createSuccess'),
            severity: 'success',
            props: { hideToast },
          });
        }}
      />
    );

  if (isEditingPermissions)
    return (
      <>
        <RolePermissionEditor
          role={selectedRole}
          rolePerms={selectedRolePerms}
          onSave={handleSaveAllPermissions}
          onCancel={() => {
            setIsEditingPermissions(false);
            setSelectedRole(null);
          }}
          onEditInfo={(r) => {
            setSelectedRole(r);
            setIsEditingInfo(true);
          }}
        />
        <ConflictDialog
          open={conflict.open}
          users={conflict.users}
          permissionName={conflict.permName}
          isAdding={conflict.isAdding}
          onClose={() => {
            setConflict({ ...conflict, open: false });
            setPendingChanges([]);
          }}
          onConfirm={handleConfirmConflict}
        />

        <RoleEditDialog
          open={isEditingInfo}
          role={selectedRole}
          onClose={() => setIsEditingInfo(false)}
          onSuccess={async () => {
            const data = await fetchRoles();
            setRoles(data);
            const updated = data.find((r) => r.id === selectedRole.id);
            if (updated) setSelectedRole(updated);
            showToast({
              message: t('rolesPage.updateSuccess'),
              severity: 'success',
              props: { hideToast },
            });
          }}
        />
      </>
    );

  return (
    <Container maxWidth="xl">
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 5 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab
            label={t('rolesPage.activeRoles')}
            value="active"
            sx={{ textTransform: 'capitalize' }}
          />
          <Tab
            label={t('rolesPage.archivedRoles')}
            value="archived"
            sx={{ textTransform: 'capitalize' }}
          />
        </Tabs>
      </Box>

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {t('rolesPage.title')}
        </Typography>

        {canCreateRole && (
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => setIsCreating(true)}
          >
            {t('rolesPage.newRole')}
          </Button>
        )}
      </Stack>

      <Card sx={{ p: 2 }}>
        <Box sx={{ py: 3, px: 1 }}>
          <StyledSearch
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('rolesPage.searchPlaceholder')}
            startAdornment={
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
          />
        </Box>
        {isLoading && roles.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <RoleTable
            roles={filteredRoles}
            activeTab={activeTab}
            // PASSING AUTHORIZATION FLAGS TO THE TABLE
            userCapabilities={{
              canUpdateRole,
              canDeleteRole,
              canManagePermissions,
            }}
            onEditInfo={(r) => {
              setSelectedRole(r);
              setIsEditingInfo(true);
            }}
            onEditPermissions={async (r) => {
              const perms = await getRolePermissionStrings(r.id);
              setSelectedRole(r);
              setSelectedRolePerms(perms);
              setIsEditingPermissions(true);
            }}
            onSync={handleBulkSync}
            onArchive={handleArchiveInitiate}
            onRestore={handleRestore}
          />
        )}
      </Card>

      <RoleEditDialog
        open={isEditingInfo}
        role={selectedRole}
        onClose={() => setIsEditingInfo(false)}
        onSuccess={loadData}
      />

      <RoleArchiveDialog
        open={isArchiveDialogOpen}
        role={selectedRole}
        availableRoles={roles.filter((r) => r.id !== selectedRole?.id && r.active)}
        onClose={() => setIsArchiveDialogOpen(false)}
        onConfirm={handleMigrationConfirm}
      />
    </Container>
  );
}
